<template>
  <footer class="bg-white">
    <div class="mx-auto w-full max-w-none px-4 sm:px-8 lg:px-12 xl:px-[120px] py-10">
      <!-- Main Content -->
      <div class="flex flex-col lg:flex-row gap-6 lg:gap-[164px] pb-10 border-b border-[#F0F1F2]">
        <!-- Column 1: Company Info -->
        <div class="flex flex-col gap-4 lg:max-w-[400px]">
          <!-- Logo -->
          <a :href="brandUrl" class="inline-block">
            <img :src="logoSrc" alt="HiCHAT" class="h-10 w-auto" />
          </a>

          <!-- Description -->
          <p class="text-sm lg:text-base font-normal leading-5 lg:leading-6 text-[#5C636C]">
            {{ companyDescription }}
          </p>

          <!-- Location -->
          <div v-if="companyInfo.address" class="flex items-start gap-2">
            <MapPin :size="20" class="flex-shrink-0 mt-0.5 text-[#8326D9]" />
            <span class="text-sm lg:text-base font-normal leading-5 lg:leading-6 text-[#0F172A]">{{ companyInfo.address }}</span>
          </div>

          <!-- Phone & Email Row -->
          <div class="flex flex-row gap-0 lg:gap-5">
            <div v-if="companyInfo.phone" class="flex items-center gap-2 w-1/2 lg:w-auto">
              <PhoneCall :size="20" class="flex-shrink-0 text-[#8326D9]" />
              <a :href="`tel:${companyInfo.phone}`" class="text-sm lg:text-base font-normal leading-5 lg:leading-6 text-[#0F172A] hover:text-[#8326D9] transition-colors">
                {{ companyInfo.phone }}
              </a>
            </div>
            <div v-if="companyInfo.email" class="flex items-center gap-2 w-1/2 lg:w-auto">
              <Mail :size="20" class="flex-shrink-0 text-[#8326D9]" />
              <a :href="`mailto:${companyInfo.email}`" class="text-sm lg:text-base font-normal leading-5 lg:leading-6 text-[#0F172A] hover:text-[#8326D9] transition-colors">
                {{ companyInfo.email }}
              </a>
            </div>
          </div>

          <!-- Social Media - PC only, inside company info with 28px gap -->
          <div v-if="socialMedia.length > 0" class="hidden lg:flex items-center gap-4 mt-[12px]">
            <a
              v-for="social in socialMedia"
              :key="social.platform"
              :href="social.url"
              :title="getSocialDisplayName(social.platform)"
              class="flex items-center justify-center w-6 h-6 rounded-full bg-[#521B85] hover:bg-[#6b23a8] transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              <SocialIcon :platform="social.platform" :size="14" class="text-white" />
            </a>
          </div>
        </div>

        <!-- Column 2: Navigation Links (2 sub-columns) - always horizontal -->
        <div class="flex-1 flex flex-row gap-1 md:gap-8 lg:gap-16 lg:pt-[56px]">
          <div v-for="(column, index) in columns" :key="index" class="flex-1">
            <!-- Column Title -->
            <h4 class="text-sm lg:text-base font-semibold leading-5 lg:leading-6 text-[#0F172A] mb-4">
              {{ column.title }}
            </h4>
            <!-- Links -->
            <ul class="flex flex-col gap-3 lg:gap-4">
              <li v-for="link in column.links" :key="link.text">
                <router-link :to="link.url" class="text-sm lg:text-base font-normal leading-5 lg:leading-6 text-[#0F172A] hover:text-[#8326D9] transition-colors">
                  {{ link.text }}
                </router-link>
              </li>
            </ul>
          </div>
        </div>

        <!-- Social Media - mobile/tablet only, below navigation -->
        <div v-if="socialMedia.length > 0" class="flex items-center gap-4 lg:hidden">
          <a
            v-for="social in socialMedia"
            :key="social.platform"
            :href="social.url"
            :title="getSocialDisplayName(social.platform)"
            class="flex items-center justify-center w-6 h-6 rounded-full bg-[#521B85] hover:bg-[#6b23a8] transition-colors"
            target="_blank"
            rel="noopener noreferrer"
          >
            <SocialIcon :platform="social.platform" :size="14" class="text-white" />
          </a>
        </div>
      </div>

      <!-- Copyright Row -->
      <div class="flex flex-col sm:flex-row items-center justify-start gap-4 pt-5">
        <p class="text-sm font-normal leading-5 text-[#5C636C]">
          {{ copyrightText }}
        </p>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { computed } from 'vue';
import SocialIcon from './SocialIcon.vue';
import { MapPin, PhoneCall, Mail } from 'lucide-vue-next';

const defaultLogo = '/brand-assets/logo_v2.svg';

const props = defineProps({
  brandName: {
    type: String,
    required: true,
  },
  brandUrl: {
    type: String,
    required: true,
  },
  logoUrl: {
    type: String,
    default: '',
  },
  supportEmail: {
    type: String,
    required: true,
  },
  content: {
    type: Object,
    default: () => ({}),
  },
});

const logoSrc = computed(() => defaultLogo);

// Footer content from API
const footerContent = computed(() => props.content || {});

// Company information
const companyInfo = computed(() => footerContent.value.company_info || {});

// Company description - use company name field or fallback
const companyDescription = computed(() => companyInfo.value.name || 'Giải pháp AI Chatbot hàng đầu dành cho doanh nghiệp hiện đại. Tự động hóa, cá nhân hóa và bứt phá tăng trưởng doanh thu 24/7.');

// Dynamic columns (2 columns max for this design)
const columns = computed(() => {
  const cols = footerContent.value.columns || [];
  return cols.slice(0, 2); // Limit to 2 columns for this design
});

// Social media links
const socialMedia = computed(() => footerContent.value.social_media || []);

// Copyright text
const copyrightText = computed(() => {
  return `© ${new Date().getFullYear()} Hilab Technology. All rights reserved.`;
});

// Get social media display name
const getSocialDisplayName = platform => {
  const nameMap = {
    facebook: 'Facebook',
    twitter: 'Twitter',
    linkedin: 'LinkedIn',
    instagram: 'Instagram',
    youtube: 'YouTube',
    tiktok: 'TikTok',
    zalo: 'Zalo',
  };
  return nameMap[platform] || platform;
};
</script>
